import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { Shape } from '../yup_types';
import { FORM_FIELDS } from '../new-application/new-application-form';

export enum BUSINESS_FORM_FIELDS {
	COMPANY_NAME = 'LegalEntityName',
	DOING_BUSINESS = 'Dba',
	ANNUAL_SALES_VOLUME = 'AnnualSalesVolume',
	DECLINE_RATE = 'DeclineRate',
	DECLINE_RECOVERY_BASELINE = 'DeclineRecoveryRateBaseline',
	BUSINESS_ESTABLISHED_DATE = 'BusinessEstablishedDate',
	TAX = 'TaxId',
	WEB_URL = 'Website',
	INDUSTRY = 'Industry',
	BUSINESS_TYPE = 'Type',
	PLATFORM = 'EcommercePlatform',
	LOGO = 'LogoFile',
	LOGO_URL = 'LogoUrl',
	MCC = 'Mcc',
	DESCRIPTOR = 'Descriptor',
	DESCRIPTION = 'Description',
	PCI_DSS = 'Pcidss',
	SITE_VERIFICATION = 'isSiteValidated',
	PARTNER_ID = 'PartnerId',
	SALES_AGENCY_ID = 'salesAgencyId',
	INTEGRATION_PARTNER = 'IntegrationPartnerId',
	ACCOUNT_ID = 'accountId',
	PRODUCTS_SOLD = 'ProductsSold',
	INTEGRATION_PARTNER_PARTICIPATE_SALE = 'IntegrationPartnerParticipateSale',
	CRM_ID = 'crmId',
	EXTERNAL_MERCHANT_IDENTIFIER = 'externalMerchantIdentifier',
}

export enum BUSINESS_FORM_FIELDS_NAMES {
	COMPANY_NAME = 'NEW_APPLICATION.LABEL.COMPANY_NAME',
	BUSINESS_ESTABLISHED_DATE = 'NEW_APPLICATION.LABEL.BUSINESS_ESTABLISHED_DATE',
	DOING_BUSINESS = 'NEW_APPLICATION.LABEL.DOING_BUSINESS',
	ANNUAL_SALES_VOLUME = 'NEW_APPLICATION.LABEL.ANNUAL_SALES_VOLUME',
	DECLINE_RATE = 'NEW_APPLICATION.LABEL.DECLINE_RATE',
	DECLINE_RECOVERY_BASELINE = 'NEW_APPLICATION.LABEL.DECLINE_RECOVERY_BASELINE',
	TAX = 'NEW_APPLICATION.LABEL.TAX',
	WEB_URL = 'NEW_APPLICATION.LABEL.WEB_URL',
	INDUSTRY = 'NEW_APPLICATION.LABEL.INDUSTRY',
	BUSINESS_TYPE = 'NEW_APPLICATION.LABEL.BUSINESS_TYPE',
	PLATFORM = 'NEW_APPLICATION.LABEL.PLATFORM',
	MCC = 'NEW_APPLICATION.LABEL.MCC',
	DESCRIPTION = 'NEW_APPLICATION.LABEL.DESCRIPTION',
	DESCRIPTOR = 'NEW_APPLICATION.LABEL.DESCRIPTOR',
	PCI_DSS = 'NEW_APPLICATION.LABEL.PCI_DSS',
	SITE_VERIFICATION = 'NEW_APPLICATION.LABEL.SITE_VERIFICATION',
	PARTNER_ID = 'NEW_APPLICATION.LABEL.PARTNER_ID',
	INTEGRATION_PARTNER = 'NEW_APPLICATION.LABEL.INTEGRATION_PARTNER',
	SALES_AGENCY_ID = 'Sales Agency',
	ACCOUNT_ID = 'Account',
	PRODUCTS_SOLD = 'NEW_APPLICATION.LABEL.PRODUCTS_SOLD',
	INTEGRATION_PARTNER_PARTICIPATE_SALE = 'NEW_APPLICATION.LABEL.INTEGRATION_PARTNER_PARTICIPATE_SALE',
	CRM_ID = 'NEW_APPLICATION.LABEL.CRM_ID',
	EXTERNAL_MERCHANT_IDENTIFIER = 'NEW_APPLICATION.LABEL.EXTERNAL_MERCHANT_IDENTIFIER',
}

export type BusinessFieldsTypes = {
	[BUSINESS_FORM_FIELDS.COMPANY_NAME]: string,
	[BUSINESS_FORM_FIELDS.DOING_BUSINESS]: string,
	[BUSINESS_FORM_FIELDS.ANNUAL_SALES_VOLUME]: string | number,
	[BUSINESS_FORM_FIELDS.DECLINE_RATE]: string | number,
	[BUSINESS_FORM_FIELDS.DECLINE_RECOVERY_BASELINE]: string | number,
	[BUSINESS_FORM_FIELDS.BUSINESS_ESTABLISHED_DATE]: string | Date | null,
	[BUSINESS_FORM_FIELDS.TAX]: string,
	[BUSINESS_FORM_FIELDS.WEB_URL]: string,
	[BUSINESS_FORM_FIELDS.INDUSTRY]: string,
	[BUSINESS_FORM_FIELDS.BUSINESS_TYPE]: string,
	[BUSINESS_FORM_FIELDS.PLATFORM]: string,
	[BUSINESS_FORM_FIELDS.MCC]: string,
	[BUSINESS_FORM_FIELDS.DESCRIPTOR]: string,
	[BUSINESS_FORM_FIELDS.PCI_DSS]: boolean,
	[BUSINESS_FORM_FIELDS.SITE_VERIFICATION]: boolean,
	[BUSINESS_FORM_FIELDS.PARTNER_ID]?: string,
	[BUSINESS_FORM_FIELDS.INTEGRATION_PARTNER]?: string,
	[BUSINESS_FORM_FIELDS.SALES_AGENCY_ID]?: string,
	[BUSINESS_FORM_FIELDS.ACCOUNT_ID]?: string,
	[BUSINESS_FORM_FIELDS.PRODUCTS_SOLD]?: string,
	[BUSINESS_FORM_FIELDS.INTEGRATION_PARTNER_PARTICIPATE_SALE]?: boolean,
	[BUSINESS_FORM_FIELDS.CRM_ID]?: string,
	[BUSINESS_FORM_FIELDS.EXTERNAL_MERCHANT_IDENTIFIER]?: string,
};

// @ts-ignore
export const getBusinessValidationSchema = (t: TFunction): Yup.SchemaOf<BusinessFieldsTypes> => Yup.object<Shape<BusinessFieldsTypes>>().shape({
	[BUSINESS_FORM_FIELDS.COMPANY_NAME]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.COMPANY_NAME_REQUIRED')),
	[BUSINESS_FORM_FIELDS.DOING_BUSINESS]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.DOING_BUSINESS_REQUIRED'))
		.max(100, t('NEW_APPLICATION.VALIDATION.DOING_BUSINESS_LENGTH')),
	[BUSINESS_FORM_FIELDS.ANNUAL_SALES_VOLUME]: Yup.number()
		.required(t('NEW_APPLICATION.VALIDATION.ANNUAL_SALES_VOLUME_REQUIRED'))
		.min(0, t('NEW_APPLICATION.VALIDATION.ANNUAL_SALES_VOLUME_MIN')),
	[BUSINESS_FORM_FIELDS.DECLINE_RATE]: Yup.number()
		// .required(t('NEW_APPLICATION.VALIDATION.DECLINE_RATE_REQUIRED'))
		.min(0, t('NEW_APPLICATION.VALIDATION.DECLINE_RATE_MIN'))
		.max(100, t('NEW_APPLICATION.VALIDATION.DECLINE_RATE_MAX')),
	[BUSINESS_FORM_FIELDS.DECLINE_RECOVERY_BASELINE]: Yup.number()
		// .required(t('NEW_APPLICATION.VALIDATION.DECLINE_RATE_REQUIRED'))
		.min(0, t('NEW_APPLICATION.VALIDATION.DECLINE_RECOVERY_BASELINE_MIN'))
		.max(100, t('NEW_APPLICATION.VALIDATION.DECLINE_RECOVERY_BASELINE_MAX')),
	[BUSINESS_FORM_FIELDS.WEB_URL]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.WEB_URL_REQUIRED'))
		.url(t('NEW_APPLICATION.VALIDATION.WEB_URL_FORMAT')),
	[BUSINESS_FORM_FIELDS.MCC]: Yup.string()
		.typeError(t('NEW_APPLICATION.VALIDATION.MCC_REQUIRED'))
		.required(t('NEW_APPLICATION.VALIDATION.MCC_REQUIRED')),
	[BUSINESS_FORM_FIELDS.PARTNER_ID]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.PARTNER_ID_REQUIRED')),
	[FORM_FIELDS.INTEGRATION_PARTNER]: Yup.string(),
});

export interface BusinessFieldsReturnTypes {
	companyName: string,
	legalEntityName: string,
	dba: string,
	annualSalesVolume: string | number,
	declineRate: number,
	declineRecoveryRateBaseline: number,
	partnerId: string,
	integrationPartnerId: string,
	taxId: string,
	website: string,
	industry: string,
	type: string,
	ecommercePlatform: string,
	mcc: string,
	descriptor: string,
	businessEstablishedDate: string | Date | null,
	pcidss: boolean,
	isSiteValidated: boolean,
	salesAgencyId: string,
	accountId: string,
	productsSold: string,
	integrationPartnerParticipateSale: boolean,
	crmId: string,
	externalMerchantIdentifier: string,
}

export const initialBusinessValues: BusinessFieldsTypes = {
	[BUSINESS_FORM_FIELDS.COMPANY_NAME]: '',
	[BUSINESS_FORM_FIELDS.DOING_BUSINESS]: '',
	[BUSINESS_FORM_FIELDS.ANNUAL_SALES_VOLUME]: '',
	[BUSINESS_FORM_FIELDS.DECLINE_RATE]: '',
	[BUSINESS_FORM_FIELDS.DECLINE_RECOVERY_BASELINE]: '',
	[BUSINESS_FORM_FIELDS.BUSINESS_ESTABLISHED_DATE]: null,
	[BUSINESS_FORM_FIELDS.TAX]: '',
	[BUSINESS_FORM_FIELDS.WEB_URL]: '',
	[BUSINESS_FORM_FIELDS.INDUSTRY]: '',
	[BUSINESS_FORM_FIELDS.BUSINESS_TYPE]: '',
	[BUSINESS_FORM_FIELDS.PLATFORM]: '',
	[BUSINESS_FORM_FIELDS.MCC]: '',
	[BUSINESS_FORM_FIELDS.DESCRIPTOR]: '',
	[BUSINESS_FORM_FIELDS.PARTNER_ID]: '',
	[BUSINESS_FORM_FIELDS.INTEGRATION_PARTNER]: '',
	[BUSINESS_FORM_FIELDS.PCI_DSS]: false,
	[BUSINESS_FORM_FIELDS.SITE_VERIFICATION]: false,
	[BUSINESS_FORM_FIELDS.PRODUCTS_SOLD]: '',
	[BUSINESS_FORM_FIELDS.CRM_ID]: '',
	[BUSINESS_FORM_FIELDS.EXTERNAL_MERCHANT_IDENTIFIER]: '',
};

export const getFormattedBusinessFormFields = (formikValues): BusinessFieldsReturnTypes => ({
	legalEntityName: formikValues?.[BUSINESS_FORM_FIELDS.COMPANY_NAME],
	companyName: formikValues?.[BUSINESS_FORM_FIELDS.COMPANY_NAME],
	dba: formikValues?.[BUSINESS_FORM_FIELDS.DOING_BUSINESS],
	annualSalesVolume: formikValues?.[BUSINESS_FORM_FIELDS.ANNUAL_SALES_VOLUME],
	declineRate: formikValues?.[BUSINESS_FORM_FIELDS.DECLINE_RATE],
	declineRecoveryRateBaseline: formikValues?.[BUSINESS_FORM_FIELDS.DECLINE_RECOVERY_BASELINE],
	partnerId: formikValues?.[BUSINESS_FORM_FIELDS.PARTNER_ID],
	integrationPartnerId: formikValues?.[BUSINESS_FORM_FIELDS.INTEGRATION_PARTNER],
	taxId: formikValues?.[BUSINESS_FORM_FIELDS.TAX],
	website: formikValues?.[BUSINESS_FORM_FIELDS.WEB_URL],
	industry: formikValues?.[BUSINESS_FORM_FIELDS.INDUSTRY],
	type: formikValues?.[BUSINESS_FORM_FIELDS.BUSINESS_TYPE] ? formikValues?.[BUSINESS_FORM_FIELDS.BUSINESS_TYPE] : '',
	mcc: formikValues?.[BUSINESS_FORM_FIELDS.MCC] ? formikValues?.[BUSINESS_FORM_FIELDS.MCC] : '',
	ecommercePlatform: formikValues?.[BUSINESS_FORM_FIELDS.PLATFORM] ? formikValues?.[BUSINESS_FORM_FIELDS.PLATFORM] : '',
	descriptor: formikValues?.[BUSINESS_FORM_FIELDS.DESCRIPTOR],
	businessEstablishedDate: formikValues?.[BUSINESS_FORM_FIELDS.BUSINESS_ESTABLISHED_DATE] ? formikValues?.[BUSINESS_FORM_FIELDS.BUSINESS_ESTABLISHED_DATE]?.toISOString() : null,
	pcidss: formikValues?.[BUSINESS_FORM_FIELDS.PCI_DSS],
	isSiteValidated: formikValues?.[BUSINESS_FORM_FIELDS.SITE_VERIFICATION],
	salesAgencyId: formikValues?.[BUSINESS_FORM_FIELDS.SALES_AGENCY_ID] ? formikValues?.[BUSINESS_FORM_FIELDS.SALES_AGENCY_ID] : '',
	accountId: formikValues?.[BUSINESS_FORM_FIELDS.ACCOUNT_ID] ? formikValues?.[BUSINESS_FORM_FIELDS.ACCOUNT_ID] : null,
	productsSold: formikValues?.[BUSINESS_FORM_FIELDS.PRODUCTS_SOLD],
	integrationPartnerParticipateSale: formikValues?.[BUSINESS_FORM_FIELDS.INTEGRATION_PARTNER_PARTICIPATE_SALE],
	crmId: formikValues?.[BUSINESS_FORM_FIELDS.CRM_ID],
	externalMerchantIdentifier: formikValues?.[BUSINESS_FORM_FIELDS.EXTERNAL_MERCHANT_IDENTIFIER],
});

export const convertToBusinessInitial = (data): BusinessFieldsTypes => ({
	[BUSINESS_FORM_FIELDS.COMPANY_NAME]: data?.legalEntityName ? data?.legalEntityName : '',
	[BUSINESS_FORM_FIELDS.DOING_BUSINESS]: data?.dba ? data?.dba : '',
	[BUSINESS_FORM_FIELDS.ANNUAL_SALES_VOLUME]: data?.annualSalesVolume ? data?.annualSalesVolume : '',
	[BUSINESS_FORM_FIELDS.DECLINE_RATE]: data?.declineRate ? data?.declineRate : '',
	[BUSINESS_FORM_FIELDS.DECLINE_RECOVERY_BASELINE]: data?.declineRecoveryRateBaseline ?? '',
	[BUSINESS_FORM_FIELDS.BUSINESS_ESTABLISHED_DATE]: data?.businessEstablishedDate ? new Date(data?.businessEstablishedDate) : null,
	[BUSINESS_FORM_FIELDS.TAX]: data?.taxId ? data?.taxId : '',
	[BUSINESS_FORM_FIELDS.WEB_URL]: data?.website ? data?.website : '',
	[BUSINESS_FORM_FIELDS.INDUSTRY]: data?.industry ? data?.industry : '',
	[BUSINESS_FORM_FIELDS.BUSINESS_TYPE]: data?.type ? data?.type : '',
	[BUSINESS_FORM_FIELDS.PLATFORM]: data?.ecommercePlatform ? data?.ecommercePlatform : '',
	[BUSINESS_FORM_FIELDS.MCC]: data?.mcc ? data?.mcc : '',
	[BUSINESS_FORM_FIELDS.PARTNER_ID]: data?.partner?.id ? data?.partner?.id : '',
	[BUSINESS_FORM_FIELDS.INTEGRATION_PARTNER]: data?.integrationPartner?.id ? data?.integrationPartner?.id : '',
	[BUSINESS_FORM_FIELDS.DESCRIPTOR]: data?.descriptor ? data?.descriptor : '',
	[BUSINESS_FORM_FIELDS.PCI_DSS]: data?.pcidss,
	[BUSINESS_FORM_FIELDS.SITE_VERIFICATION]: data?.isSiteValidated,
	[BUSINESS_FORM_FIELDS.SALES_AGENCY_ID]: data?.salesAgency?.id ? data?.salesAgency?.id : '',
	[BUSINESS_FORM_FIELDS.ACCOUNT_ID]: data?.accountId,
	[BUSINESS_FORM_FIELDS.PRODUCTS_SOLD]: data?.productsSold,
	[BUSINESS_FORM_FIELDS.INTEGRATION_PARTNER_PARTICIPATE_SALE]: data?.integrationPartnerParticipateSale,
	[BUSINESS_FORM_FIELDS.CRM_ID]: data?.crmId ? data?.crmId : '',
	[BUSINESS_FORM_FIELDS.EXTERNAL_MERCHANT_IDENTIFIER]: data?.externalMerchantIdentifier ? data?.externalMerchantIdentifier : '',
});

export const getFormattedBusinessData = (data): BusinessFieldsReturnTypes => ({
	legalEntityName: data?.legalEntityName ?? '',
	companyName: data?.companyName ?? '',
	dba: data?.dba ?? '',
	annualSalesVolume: data?.annualSalesVolume ?? '',
	declineRate: data?.declineRate ?? '',
	declineRecoveryRateBaseline: data?.declineRecoveryRateBaseline ?? '',
	partnerId: data?.partner?.id ? data?.partner?.id : '',
	integrationPartnerId: data?.integrationPartner?.id ? data?.integrationPartner?.id : '',
	salesAgencyId: data?.salesAgency?.id ? data?.salesAgency?.id : '',
	taxId: data?.taxId,
	website: data?.website,
	industry: data?.industry,
	type: data?.type,
	mcc: data?.mcc,
	ecommercePlatform: data?.ecommercePlatform,
	descriptor: data?.descriptor,
	businessEstablishedDate: data?.businessEstablishedDate,
	pcidss: data?.pcidss,
	isSiteValidated: data?.isSiteValidated,
	accountId: data?.accountId,
	productsSold: data?.productsSold,
	integrationPartnerParticipateSale: data?.integrationPartnerParticipateSale,
	crmId: data?.crmId,
	externalMerchantIdentifier: data?.externalMerchantIdentifier,
});
